import React, { useEffect, useMemo, useRef } from 'react';

import { useSelector } from 'react-redux';

import { RootState } from '../../store';
import ThreadList, { lastLine } from './Messenger/ThreadList';
import ThreadScreen from './Messenger/ThreadScreen';
import PhoneBanner, { arrivalLine } from './PhoneBanner';
import PhoneGlyph from './PhoneGlyph';
import PhoneHome from './PhoneHome';
import PhoneStatusBar from './PhoneStatusBar';
import { phonePaths } from './phoneRoutes';
import { phoneStyleVars } from './phoneStyle';
import type { PhoneController } from './usePhoneController';

import './styles/index.scss';

export interface Props {
  phone: PhoneController;
}

/**
 * The pretend phone's own screens, laid over the trail: the home screen, the
 * messenger, and the banner that announces a new message. The map, the
 * scanner and every full-screen page are the player's existing ones; the
 * phone only decides what is in front of them.
 */
const PhoneOverlays: React.FC<Props> = ({ phone }) => {
  const { shell, story, base, screen, threads, apps } = phone;

  const viewportHeight = useSelector(
    (state: RootState) => state.ui.visualViewport?.height
  );
  const style = useMemo(
    () => phoneStyleVars(shell, viewportHeight),
    [shell, viewportHeight]
  );

  const messengerLabel =
    shell.apps.find((app) => app.id === 'messages')?.label ??
    shell.themeSpec.messengerName;
  const mapLabel =
    shell.apps.find((app) => app.id === 'map')?.label ??
    shell.themeSpec.mapName;

  const openThread =
    screen.screen === 'thread'
      ? threads.find((thread) => thread.key === screen.threadKey)
      : undefined;

  const home = shell.layout === 'home';
  const parentOfMessages = base;

  let body: React.ReactNode = null;
  if (screen.screen === 'base' && home) {
    const newest = threads[0];
    body = (
      <PhoneHome
        shell={shell}
        storyTitle={story.title}
        apps={apps}
        latest={
          newest && {
            name: newest.name,
            avatar: newest.avatar,
            group: newest.isGroup,
            line: lastLine(newest),
            unread: newest.unreadCount,
            onOpen: () => phone.open(phonePaths.thread(base, newest.key)),
          }
        }
      />
    );
  } else if (
    screen.screen === 'messages' ||
    (screen.screen === 'thread' && !openThread)
  ) {
    // A thread link that names nothing (not delivered yet, or removed by a
    // live edit) falls back to the list rather than an empty screen.
    body = (
      <ThreadList
        threads={threads}
        shell={shell}
        title={messengerLabel}
        onBack={() => phone.up(parentOfMessages)}
        onOpen={(key) => phone.open(phonePaths.thread(base, key))}
      />
    );
  } else if (screen.screen === 'thread' && openThread) {
    body = (
      <ThreadScreen
        key={openThread.key}
        thread={openThread}
        shell={shell}
        points={story.storyPoints}
        onBack={() => phone.up(phonePaths.messages(base))}
        onOpenPage={(code, index) =>
          phone.open(phonePaths.messagePage(base, code, index))
        }
        onOpenDossier={(code) => phone.open(phonePaths.trailPoint(base, code))}
        onShowOnMap={phone.showOnMap}
      />
    );
  }

  const showMapBar = home && screen.screen === 'map';

  // What this screen is, for the region's name and the browser's title.
  const screenName =
    screen.screen === 'thread' && openThread
      ? openThread.name
      : screen.screen === 'messages' || screen.screen === 'thread'
        ? messengerLabel
        : screen.screen === 'map'
          ? mapLabel
          : 'Home screen';
  useEffect(() => {
    const before = document.title;
    document.title = `${screenName} · ${story.title}`;
    return () => {
      document.title = before;
    };
  }, [screenName, story.title]);

  // Moving between phone screens swaps the whole view: put focus at the top
  // of the new one, or a keyboard or screen reader user is left on a button
  // that no longer exists.
  const deviceRef = useRef<HTMLDivElement | null>(null);
  const screenKey =
    screen.screen === 'thread' ? `thread:${screen.threadKey}` : screen.screen;
  const firstScreenRef = useRef(true);
  useEffect(() => {
    if (firstScreenRef.current) {
      firstScreenRef.current = false;
      return;
    }
    deviceRef.current?.focus({ preventScroll: true });
  }, [screenKey]);

  return (
    <div
      className={`Phone ${body ? 'Phone-covering' : ''}`}
      data-ph-theme={shell.theme}
      data-ph-layout={shell.layout}
      style={style}
    >
      {body && (
        <div
          className="Phone_device"
          ref={deviceRef}
          tabIndex={-1}
          role="region"
          aria-label={screenName}
          onKeyDown={(event) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            if (screen.screen === 'thread') phone.up(phonePaths.messages(base));
            else if (screen.screen === 'messages') phone.up(base);
          }}
        >
          <PhoneStatusBar deviceName={shell.themeSpec.deviceName} />
          {body}
        </div>
      )}
      {showMapBar && (
        <div className="Phone_mapBar">
          <button
            type="button"
            className="Phone_mapBarHome"
            onClick={() => phone.up(base)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M15 5 8 12l7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>
          <span className="Phone_mapBarTitle">
            <PhoneGlyph glyph="map" theme={shell.theme} />
            {mapLabel}
          </span>
        </div>
      )}
      {/* Always mounted, and only its TEXT changes: a status region that
          arrives together with its words is often never read out. Kept apart
          from the banner's buttons so "Dismiss" isn't read as the message. */}
      <span className="sr-only" role="status">
        {phone.arrival
          ? `New message from ${phone.arrival.message.sender.name}: ${arrivalLine(phone.arrival)}`
          : ''}
      </span>
      {phone.arrival && (
        <PhoneBanner
          arrival={phone.arrival}
          onDismiss={phone.dismissArrival}
          onOpen={() => {
            const key = phone.arrival?.threadKey;
            phone.dismissArrival();
            if (key) phone.open(phonePaths.thread(base, key));
          }}
        />
      )}
    </div>
  );
};

export default PhoneOverlays;
